import React from 'react';
import { Clock, Cloud, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Connector } from '../services/api';
import './ConnectorCard.css';

interface ConnectorCardProps {
  connector: Connector;
  isSelected: boolean;
  onClick: () => void;
  isCreating?: boolean;
}

const ConnectorCard: React.FC<ConnectorCardProps> = ({
  connector,
  isSelected,
  onClick,
  isCreating = false
}) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getStatusIcon = () => {
    switch (connector.status) {
      case 'active':
        return <CheckCircle size={14} className="status-icon active" />;
      case 'failed':
        return <XCircle size={14} className="status-icon failed" />;
      case 'testing':
        return <RefreshCw size={14} className="status-icon testing spinning" />;
      default:
        return <AlertCircle size={14} className="status-icon disabled" />;
    }
  };

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'gcs':
        return 'Google Cloud Storage';
      case 's3':
        return 'Amazon S3';
      case 'azure':
        return 'Azure Blob Storage';
      default:
        return provider;
    }
  };

  const getProviderShortLabel = (provider: string) => {
    switch (provider) {
      case 'gcs':
        return 'GCS';
      case 's3':
        return 'S3';
      case 'azure':
        return 'Azure';
      default:
        return provider;
    }
  };

  const scopes = connector.scopes && connector.scopes.length > 0 ? connector.scopes : ['import'];

  return (
    <div className="connector-card-wrapper">
      <div
        className={`connector-card ${isSelected ? 'selected' : ''} ${isCreating ? 'creating' : ''} ${connector.status}`}
        onClick={onClick}
      >
        {/* Top color bar */}
        <div className="connector-card-colorbar" />

        {/* Header */}
        <div className="connector-card-header">
          <div className="connector-card-title">
            <Cloud size={18} className="cloud-icon" />
            <span className="connector-name">{connector.name || 'New Connector'}</span>
          </div>
          <div className="connector-status-badge">
            {getStatusIcon()}
            <span className="status-text">{connector.status}</span>
          </div>
        </div>

        {/* Provider & Bucket */}
        <div className="connector-card-details">
          <div className="provider-badge">
            <span className={`provider-label ${connector.provider}`}>{getProviderShortLabel(connector.provider)}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            {scopes.map((scope) => (
              <span
                key={scope}
                style={{
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  padding: '0.05rem 0.35rem',
                  borderRadius: '999px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                }}
              >
                {scope}
              </span>
            ))}
          </div>
          <div className="bucket-info">
            <span className="bucket-label">
              {connector.bucket || connector.container || 'Not configured'}
            </span>
          </div>
        </div>

        {/* Connection Info Section */}
        <div className="connector-card-info-section">
          <div className="info-row">
            <span className="info-label">Provider</span>
            <span className="info-value">{getProviderLabel(connector.provider)}</span>
          </div>
          {connector.gcp_project_id && (
            <div className="info-row">
              <span className="info-label">Project</span>
              <span className="info-value">{connector.gcp_project_id}</span>
            </div>
          )}
          {connector.region && (
            <div className="info-row">
              <span className="info-label">Region</span>
              <span className="info-value">{connector.region}</span>
            </div>
          )}
          {connector.storage_account && (
            <div className="info-row">
              <span className="info-label">Account</span>
              <span className="info-value">{connector.storage_account}</span>
            </div>
          )}
        </div>

        {/* Error Display */}
        {connector.last_test_error && (
          <div className="connector-card-error">
            <AlertCircle size={12} />
            <span>{connector.last_test_error.substring(0, 60)}...</span>
          </div>
        )}

        {/* Footer */}
        <div className="connector-card-footer">
          <div className="connector-card-meta">
            <Clock size={12} />
            <span>{isCreating ? 'Draft' : formatDate(connector.created_at)}</span>
          </div>
          <div className="connector-card-last-tested">
            {connector.last_tested_at ? (
              <span className="tested-time" title={`Last tested: ${new Date(connector.last_tested_at).toLocaleString()}`}>
                Tested {formatRelativeTime(connector.last_tested_at)}
              </span>
            ) : (
              <span className="never-tested">Not tested</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectorCard;
